import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { scribeHistory, todayScribeHistory } from './ScribeData';
import ScribeDetailsPanel from './ScribeDetailsPanel';
import ScribeLettersPanel from './ScribeLettersPanel';
import { ScribeStatus, UserContext, useScribeContext } from 'op-contexts/index';
import { ScribeHistoryItem } from './Interface';
import LoadingSpinner from 'shared-components/components/LoadingSpinner/LoadingSpinner';
import dayjs from 'dayjs';
import { CurrentAppConfig } from '../Careplan/AppConfig';

const ReviewAndSendDialog = ({
  handleSend,
  open,
  handleClose,
}: {
  handleSend: () => void;
  open: boolean;
  handleClose: () => void;
}): JSX.Element => {
  const [initialConsultNote, setInitialConsultNote] = useState('Saved as clinical note');
  const [initialConsultLetter, setInitialConsultLetter] = useState([
    'Dr Stapleton (Referring)',
    'Dr Williams (Primary)',
  ]);
  const [summaryToPatient, setSummaryToPatient] = useState('Send to Patient(Jonathan.w382@gmail.com)');
  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>Review and Send</DialogTitle>
      <DialogContent>
        <Typography>Are you ready to finalise and distribute these outputs?</Typography>
        <Stack direction="column" gap={2} sx={{ marginTop: '16px' }}>
          <FormControl>
            <InputLabel id="label-initial-consult-note">Initial Consult Note</InputLabel>
            <Select
              labelId="label-initial-consult-note"
              id="select-initial-consult-note"
              label="Initial Consult Note"
              variant="outlined"
              value={initialConsultNote}
              onChange={(event) => setInitialConsultNote(event.target.value)}>
              <MenuItem value="Saved as clinical note">Saved as clinical note</MenuItem>
            </Select>
          </FormControl>
          <FormControl>
            <InputLabel id="label-initial-consult-letter">Initial Consult Letter Send to (Physician)</InputLabel>
            <Select
              multiple
              labelId="label-initial-consult-letter"
              id="select-initial-consult-letter"
              label="Initial Consult Letter Send to (Physician)"
              variant="outlined"
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} />
                  ))}
                </Box>
              )}
              value={initialConsultLetter}
              onChange={(event) => setInitialConsultLetter(event.target.value as string[])}>
              <MenuItem value="Dr Stapleton (Referring)">Dr Stapleton (Referring)</MenuItem>
              <MenuItem value="Dr Williams (Primary)">Dr Williams (Primary)</MenuItem>
            </Select>
          </FormControl>
          <FormControl>
            <InputLabel id="label-summary-to-patient">Summary for Patient</InputLabel>
            <Select
              labelId="label-summary-to-patient"
              id="select-summary-to-patient"
              label="Summary for Patient"
              variant="outlined"
              value={summaryToPatient}
              onChange={(event) => setSummaryToPatient(event.target.value)}>
              <MenuItem value="Send to Patient(Jonathan.w382@gmail.com)">
                Send to Patient(Jonathan.w382@gmail.com)
              </MenuItem>
            </Select>
          </FormControl>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
          This version is written in patient-friendly language. Please review before sending.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="text">
          CANCEL
        </Button>
        <Button onClick={handleSend} variant="text">
          SEND
        </Button>
      </DialogActions>
    </Dialog>
  );
};
const ScribeContainer = (): JSX.Element => {
  const [selectedScribe, setSelectedScribe] = useState(2);
  const [scribeSent, setScribeSent] = useState(false);
  const [reviewAndSendDialogOpen, setReviewAndSendDialogOpen] = useState(false);
  const theme = useTheme();
  const { isScribing, timer } = useScribeContext();
  const [scribeList, setScribeList] = useState(scribeHistory);
  const [loading, setLoading] = useState(false);
  const { state } = useContext(UserContext);

  useEffect(() => {
    if (isScribing === ScribeStatus.STOPPED && timer !== 0) {
      setLoading(true);
      const timeoutId = setTimeout(() => {
        setLoading(false);
        const todayScribe = [
          {
            ...todayScribeHistory[0],
            request: {
              ...todayScribeHistory[0].request,
              text: todayScribeHistory[0].request?.text
                .replaceAll('(me)', state.name)
                .replaceAll('(today)', dayjs().format(CurrentAppConfig.RadiationDashboard.dateTimeFormat)),
              outputs: todayScribeHistory[0].request?.outputs.map((output) => ({
                ...output,
                text: output.text.replace('(me)', state.name),
              })),
              createdAt: dayjs().format(CurrentAppConfig.RadiationDashboard.dateTimeFormat),
            },
          },
        ] as ScribeHistoryItem[];
        setScribeList([...scribeHistory, ...todayScribe]);
      }, 3000);
      return () => clearTimeout(timeoutId);
    }
  }, [isScribing, scribeHistory, todayScribeHistory, state.name]);

  useEffect(() => {
    if (scribeSent) {
      const todayScribe = [
        {
          ...todayScribeHistory[0],
          subTitle: 'Submitted',
          request: {
            ...todayScribeHistory[0].request,
            text: todayScribeHistory[0].request?.text
              .replaceAll('(me)', state.name)
              .replaceAll('(today)', dayjs().format(CurrentAppConfig.RadiationDashboard.dateTimeFormat)),
            outputs: todayScribeHistory[0].request?.outputs.map((output) => ({
              ...output,
              text: output.text.replace('(me)', state.name),
            })),
            createdAt: dayjs().format(CurrentAppConfig.RadiationDashboard.dateTimeFormat),
          },
        },
      ] as ScribeHistoryItem[];
      setScribeList([...scribeHistory, ...todayScribe]);
    }
  }, [scribeSent]);

  const handleDeleteLetter = (index: number) => {
    const newScribeList = [...scribeList];
    if (newScribeList[selectedScribe]?.request?.outputs) {
      newScribeList[selectedScribe].request.outputs = newScribeList[selectedScribe].request.outputs.filter(
        (_, i) => i !== index,
      );
    }
    setScribeList(newScribeList);
  };
  return (
    <Stack direction="row" width="100%" flex={1}>
      <Stack direction="column" width="260px" sx={{ borderRight: '1px solid #e0e0e0', paddingTop: '8px' }}>
        {scribeList.map((item, index) =>
          item.title === 'Today' ? (
            <ListSubheader key={index} sx={{ height: '56px' }}>
              {item.title}
            </ListSubheader>
          ) : (
            <Button
              key={index}
              variant="text"
              onClick={() => setSelectedScribe(index)}
              sx={{
                backgroundColor: selectedScribe !== index ? 'transparent' : theme.palette.action.selected,
                borderRadius: '4px',
                padding: '8px 16px',
                justifyContent: 'flex-start',
              }}>
              <Stack direction="column">
                <Typography
                  variant="body2"
                  sx={{
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    display: 'inline-block',
                    textAlign: 'left',
                    textTransform: 'none',
                  }}>
                  {item.title}
                </Typography>
                {item.subTitle && (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                    Status: {item.subTitle}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                  {item.request?.createdAt}
                </Typography>
              </Stack>
            </Button>
          ),
        )}
      </Stack>
      {loading ? (
        <LoadingSpinner loadingText={'Loading Scribe Letters'} relativeSpinner={true} />
      ) : (
        <Grid container>
          <Grid item xs={12} sx={{ borderLeft: '1px solid #e0e0e0', height: '100%' }}>
            <ScribeLettersPanel
              scribe={scribeList[selectedScribe]}
              scribeSent={scribeSent}
              handleSend={() => setReviewAndSendDialogOpen(true)}
              handleDeleteLetter={handleDeleteLetter}
            />
          </Grid>
        </Grid>
      )}
      <ReviewAndSendDialog
        handleSend={() => {
          setScribeSent(true);
          setReviewAndSendDialogOpen(false);
        }}
        open={reviewAndSendDialogOpen}
        handleClose={() => setReviewAndSendDialogOpen(false)}
      />
    </Stack>
  );
};

export default ScribeContainer;
